import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { createSite, updateSite } from '../../../api/backendApi';
import { getSiteWeather } from '../../../api/weatherApi';

// Common timezone options
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC',
];

export const WEATHER_LOCATION_MAX_LEN = 200;

/**
 * Build editor form settings from a site document without dropping unknown keys.
 * @param {object|null|undefined} siteSettings
 */
export function buildSiteEditorSettings(siteSettings) {
  const base =
    siteSettings && typeof siteSettings === 'object' && !Array.isArray(siteSettings)
      ? { ...siteSettings }
      : {};

  const weatherLocation =
    base.weatherLocation && typeof base.weatherLocation === 'object' && !Array.isArray(base.weatherLocation)
      ? { ...base.weatherLocation }
      : {};

  return {
    ...base,
    timezone: base.timezone || 'America/Toronto',
    notes: typeof base.notes === 'string' ? base.notes : base.notes || '',
    weatherLocation: {
      ...weatherLocation,
      query:
        typeof weatherLocation.query === 'string'
          ? weatherLocation.query
          : weatherLocation.query == null
            ? ''
            : String(weatherLocation.query),
    },
  };
}

/**
 * Normalize settings for create/update payload.
 * Blank weather query clears only settings.weatherLocation.query.
 */
export function buildSiteSettingsPayload(settings) {
  const base =
    settings && typeof settings === 'object' && !Array.isArray(settings)
      ? { ...settings }
      : {};

  const timezone = String(base.timezone || '').trim();
  const notes = typeof base.notes === 'string' ? base.notes : base.notes || '';

  const existingWl =
    base.weatherLocation && typeof base.weatherLocation === 'object' && !Array.isArray(base.weatherLocation)
      ? { ...base.weatherLocation }
      : {};
  const query = String(existingWl.query ?? '').trim();

  const next = {
    ...base,
    timezone,
    notes,
  };

  if (query) {
    next.weatherLocation = {
      ...existingWl,
      query,
    };
  } else {
    // Explicit blank query so merge-safe backend clears only `.query`
    // while preserving sibling weatherLocation keys and other settings.
    next.weatherLocation = {
      ...existingWl,
      query: '',
    };
  }

  return next;
}

export function validateWeatherLocationInput(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.length > WEATHER_LOCATION_MAX_LEN) {
    return `Weather Location must be at most ${WEATHER_LOCATION_MAX_LEN} characters`;
  }
  return null;
}

const emptyForm = () => ({
  name: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Canada',
  },
  settings: buildSiteEditorSettings({
    timezone: 'America/Toronto',
    notes: '',
  }),
});

const SiteEditorDialog = ({ open, site, tenantId, onClose, onSuccess, onError }) => {
  const isEdit = Boolean(site);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [testingWeather, setTestingWeather] = useState(false);
  const [weatherTestResult, setWeatherTestResult] = useState(null);
  const [weatherTestError, setWeatherTestError] = useState(null);

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name || '',
        address: {
          line1: site.address?.line1 || '',
          line2: site.address?.line2 || '',
          city: site.address?.city || '',
          state: site.address?.state || '',
          postalCode: site.address?.postalCode || '',
          country: site.address?.country || 'USA',
        },
        settings: buildSiteEditorSettings(site.settings),
      });
    } else {
      setFormData(emptyForm());
    }
    setErrors({});
    setWeatherTestResult(null);
    setWeatherTestError(null);
  }, [site, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
    const errorKey = `address.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
    const errorKey = `settings.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleWeatherLocationChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        weatherLocation: {
          ...(prev.settings?.weatherLocation || {}),
          query: value,
        },
      },
    }));
    if (errors['settings.weatherLocation.query']) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors['settings.weatherLocation.query'];
        return newErrors;
      });
    }
    setWeatherTestResult(null);
    setWeatherTestError(null);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Site name is required';
    }

    if (!formData.address.line1?.trim()) {
      newErrors['address.line1'] = 'Address line 1 is required';
    }

    if (!formData.address.city?.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state?.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.postalCode?.trim()) {
      newErrors['address.postalCode'] = 'Postal code is required';
    }

    if (!formData.address.country?.trim()) {
      newErrors['address.country'] = 'Country is required';
    }

    if (!formData.settings.timezone?.trim()) {
      newErrors['settings.timezone'] = 'Timezone is required';
    }

    const weatherErr = validateWeatherLocationInput(formData.settings?.weatherLocation?.query);
    if (weatherErr) {
      newErrors['settings.weatherLocation.query'] = weatherErr;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    const payload = {
      ...formData,
      settings: buildSiteSettingsPayload(formData.settings),
    };

    try {
      if (isEdit) {
        const response = await updateSite(site.id, payload, tenantId);
        if (response.success) {
          onSuccess(`Site "${formData.name}" updated successfully`);
        } else {
          onError(response.error || 'Failed to update site');
        }
      } else {
        const response = await createSite(payload, tenantId);
        if (response.success) {
          onSuccess(
            `Site "${formData.name}" created successfully`,
            response.data || { id: response.siteId, name: formData.name },
          );
        } else {
          onError(response.error || 'Failed to create site');
        }
      }
    } catch (error) {
      console.error('Error saving site:', error);
      onError(error.message || 'An error occurred while saving the site');
    } finally {
      setSaving(false);
    }
  };

  const savedWeatherQuery = String(site?.settings?.weatherLocation?.query || '').trim();
  const draftWeatherQuery = String(formData.settings?.weatherLocation?.query || '').trim();
  const weatherDraftDirty = isEdit && savedWeatherQuery !== draftWeatherQuery;

  const handleTestWeather = async () => {
    if (!isEdit || !site?.id) return;
    setTestingWeather(true);
    setWeatherTestResult(null);
    setWeatherTestError(null);
    try {
      const response = await getSiteWeather(site.id, { refresh: true });
      const data = response?.data || response;
      const location = [data?.location?.name, data?.location?.region].filter(Boolean).join(', ');
      const temp =
        data?.current?.temperatureC == null ? '—' : `${Math.round(Number(data.current.temperatureC))}°C`;
      const condition = data?.current?.condition || '—';
      setWeatherTestResult(`${location || 'Weather'} · ${temp} · ${condition}`);
    } catch (error) {
      const message = String(error?.message || error || 'Weather test failed');
      if (message.includes('WEATHER_LOCATION_MISSING') || /not configured/i.test(message)) {
        setWeatherTestError('Weather is not configured for the saved site location.');
      } else {
        setWeatherTestError('Unable to load weather for the saved site configuration.');
      }
    } finally {
      setTestingWeather(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 2000 }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isEdit ? 'Edit Site' : 'Add Site'}
          {site?.isDefault && (
            <Chip label="Default" color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Site Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name}
              required
            />
            <TextField
              fullWidth
              size="small"
              select
              label="Timezone"
              value={formData.settings.timezone}
              onChange={(e) => handleSettingsChange('timezone', e.target.value)}
              error={Boolean(errors['settings.timezone'])}
              helperText={errors['settings.timezone']}
              required
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Address Line 1"
            value={formData.address.line1}
            onChange={(e) => handleAddressChange('line1', e.target.value)}
            error={Boolean(errors['address.line1'])}
            helperText={errors['address.line1']}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Address Line 2"
            value={formData.address.line2}
            onChange={(e) => handleAddressChange('line2', e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={formData.address.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              error={Boolean(errors['address.city'])}
              helperText={errors['address.city']}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Province/State"
              value={formData.address.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              error={Boolean(errors['address.state'])}
              helperText={errors['address.state']}
              required
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Postal Code"
              value={formData.address.postalCode}
              onChange={(e) => handleAddressChange('postalCode', e.target.value)}
              error={Boolean(errors['address.postalCode'])}
              helperText={errors['address.postalCode']}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Country"
              value={formData.address.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              error={Boolean(errors['address.country'])}
              helperText={errors['address.country']}
              required
            />
          </Box>

          <Box data-testid="site-editor-weather-section" sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              label="Weather Location"
              value={formData.settings?.weatherLocation?.query || ''}
              onChange={(e) => handleWeatherLocationChange(e.target.value)}
              error={Boolean(errors['settings.weatherLocation.query'])}
              helperText={
                errors['settings.weatherLocation.query'] ||
                'Optional. City, postal code, or lat,long'
              }
              placeholder="Carleton Place, Ontario"
              inputProps={{ 'data-testid': 'site-editor-weather-location' }}
            />
            {isEdit ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleTestWeather}
                disabled={testingWeather || saving}
                startIcon={testingWeather ? <CircularProgress size={16} /> : null}
                data-testid="site-editor-test-weather"
                sx={{ mt: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {testingWeather ? 'Testing…' : 'Test Weather'}
              </Button>
            ) : null}
          </Box>
          {isEdit && weatherDraftDirty ? (
            <Alert severity="info">
              Save before testing a new weather location.
            </Alert>
          ) : null}
          {weatherTestResult ? (
            <Alert severity="success" data-testid="site-editor-weather-test-result">
              {weatherTestResult}
            </Alert>
          ) : null}
          {weatherTestError ? (
            <Alert severity="warning" data-testid="site-editor-weather-test-error">
              {weatherTestError}
            </Alert>
          ) : null}

          <TextField
            fullWidth
            size="small"
            label="Notes"
            value={formData.settings.notes}
            onChange={(e) => handleSettingsChange('notes', e.target.value)}
            multiline
            minRows={2}
            maxRows={3}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteEditorDialog;
